import {useEffect} from 'react';
import {Linking, Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import analytics from '@react-native-firebase/analytics';
import {PlayInstallReferrer, PlayInstallReferrerInfo} from 'react-native-play-install-referrer';
import {api} from '../services/api';
import {getPersistentDeviceId} from '../utils/deviceId';
import {getAppsFlyerId} from '../lib/appsflyer';
import {
  AttributionSource,
  UtmParams,
  determineSource,
  parseUTMsFromString,
  queryStringOf,
} from './attributionParams';
import {version} from '../../package.json';

const INSTALL_ATTRIBUTION_KEY = 'install_attribution_data';

const INSTALL_REFERRER_RESOLVED_KEY = 'install_referrer_resolved';

export interface InstallAttributionData extends UtmParams {
  nativeDeviceId: string;
  appsflyer_id: string | null;
  platform: 'android' | 'ios' | 'web';
  app_version: string;
  os_version: string;
  timestamp: string;

  install_method: 'referrer' | 'deep_link' | 'organic';

  source: AttributionSource;

  url: string;
  referrer_raw?: string;
}

// Lê o referrer de instalação da Play Store via react-native-play-install-referrer.
// `success: false` indica falha transitória na consulta (não conseguimos determinar nada ainda).
function getPlayInstallReferrer(): Promise<{
  success: boolean;
  info: PlayInstallReferrerInfo | null;
}> {
  return new Promise(resolve => {
    PlayInstallReferrer.getInstallReferrerInfo((info, error) => {
      if (error) {
        console.log('🔗 Error getting install referrer:', error.message);
        resolve({success: false, info: null});
        return;
      }

      resolve({success: true, info});
    });
  });
}

// Tenta resolver o referrer de instalação (Android apenas). Retorna `null` tanto quando
// já sabemos que é orgânico quanto quando ainda não foi possível determinar — nesse
// segundo caso, `INSTALL_REFERRER_RESOLVED_KEY` não é gravada e a checagem se repete na
// próxima abertura, evitando perder a atribuição por uma falha pontual da API.
async function resolveInstallReferrer(): Promise<string | null> {
  if (Platform.OS !== 'android') {
    return null;
  }

  const alreadyResolved = await AsyncStorage.getItem(INSTALL_REFERRER_RESOLVED_KEY);
  if (alreadyResolved !== null) {
    return alreadyResolved === '__organic__' ? null : alreadyResolved;
  }

  const {success, info} = await getPlayInstallReferrer();
  if (!success) {
    return null;
  }

  // Não dar toLowerCase no referrer inteiro: gclid e fbclid são case-sensitive.
  // A normalização serve só para as comparações abaixo.
  const referrer = info?.installReferrer?.trim() || '';
  const normalized = referrer.toLowerCase();
  const isOrganic =
    !referrer || normalized === 'organic' || normalized === 'utm_source=(not+set)';

  await AsyncStorage.setItem(
    INSTALL_REFERRER_RESOLVED_KEY,
    isOrganic ? '__organic__' : referrer,
  );

  if (normalized === 'picpay') {
    analytics().logEvent('instalacao_referrer_picpay');
  }

  return isOrganic ? null : referrer;
}

// Primeira captura da atribuição de instalação (roda uma única vez, controlada pelo
// AsyncStorage). Se o referrer já tiver sido resolvido nesta mesma execução, o envio sai
// combinado num único POST.
async function captureInstallAttribution(nativeDeviceId: string, referrer: string | null) {
  const data: InstallAttributionData = {
    nativeDeviceId,
    appsflyer_id: await getAppsFlyerId(),
    platform: Platform.OS as 'android' | 'ios' | 'web',
    app_version: version,
    os_version: String(Platform.Version),
    timestamp: new Date().toISOString(),
    install_method: 'organic',
    source: 'organic',
    url: '',
  };

  // 1. Play Install Referrer — captura UTMs preservados pela Play Store (ou 'picpay')
  if (referrer) {
    const isPicPay = referrer.toLowerCase() === 'picpay';
    const utms = isPicPay ? {} : parseUTMsFromString(referrer);

    Object.assign(data, utms, {
      install_method: 'referrer' as const,
      source: isPicPay ? ('picpay' as const) : determineSource('', utms),
      referrer_raw: referrer,
    });
  } else {
    // 2. Fallback: URL direta (links de bio, indicação, deep links)
    const url = await Linking.getInitialURL();
    console.log('🔗 Initial URL:', url);

    if (url) {
      data.url = url;
      data.install_method = 'deep_link';
      // Não trocar por `new URL(url)`: `URL.search` lança "is not implemented" no RN.
      const utms = parseUTMsFromString(queryStringOf(url));
      Object.assign(data, utms, {
        source: determineSource(url, utms),
      });
    }
  }

  api.post('/instalacao', data).catch(() => {});
  await AsyncStorage.setItem(INSTALL_ATTRIBUTION_KEY, JSON.stringify(data));
  console.log('🔗 Install attribution captured:', data);
}

// Chamado quando a atribuição principal já tinha sido capturada (e enviada) antes do
// referrer ser resolvido — atualiza o dado local e reporta o referrer separadamente,
// já que o POST original já saiu sem essa informação.
async function attachResolvedReferrer(
  nativeDeviceId: string,
  existingRaw: string,
  referrer: string,
) {
  try {
    const existingData: InstallAttributionData = JSON.parse(existingRaw);
    const isPicPay = referrer.toLowerCase() === 'picpay';
    const utms = isPicPay ? {} : parseUTMsFromString(referrer);
    const newSource = isPicPay ? ('picpay' as const) : determineSource('', utms);

    if (existingData.source !== newSource) {
      Object.assign(existingData, utms, {
        install_method: 'referrer' as const,
        source: newSource,
        referrer_raw: referrer,
      });
      await AsyncStorage.setItem(INSTALL_ATTRIBUTION_KEY, JSON.stringify(existingData));
    }
  } catch (error) {
    console.log('🔗 Error updating stored attribution with resolved referrer:', error);
  }

  api.post('/instalacao', {nativeDeviceId, referrer}).catch(() => {});
}

export function useInstallAttribution() {
  useEffect(() => {
    const run = async () => {
      try {
        const nativeDeviceId = await getPersistentDeviceId();
        const referrer = await resolveInstallReferrer();

        const existingRaw = await AsyncStorage.getItem(INSTALL_ATTRIBUTION_KEY);

        if (!existingRaw) {
          await captureInstallAttribution(nativeDeviceId, referrer);
          return;
        }

        console.log('🔗 Install attribution already captured');

        if (referrer) {
          await attachResolvedReferrer(nativeDeviceId, existingRaw, referrer);
        }
      } catch (error) {
        console.error('🔗 Error capturing install attribution:', error);
      }
    };

    run();
  }, []);
}

export async function getInstallAttributionData(): Promise<InstallAttributionData | null> {
  try {
    const data = await AsyncStorage.getItem(INSTALL_ATTRIBUTION_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('🔗 Error getting install attribution data:', error);
    return null;
  }
}
